/**
 * API hooks for the Virtual Library — real backend only.
 * All calls delegate to the real LibraryApiController endpoints via libraryApi.ts.
 * Transformation helpers normalise the API shapes to the internal UI shapes.
 */
import React from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import type {
  Category,
  ResourcesQueryParams,
  FlatTreeNode,
  Role,
  CreateCategoryPayload,
  CreateAdminResourcePayload,
  UpdateAdminResourcePayload,
} from 'types';
import type { SearchCategoryMatch, ExcludedLocation } from 'types/LibraryApi';
import {
  transformNavCategories,
  resolveResourceUrl,
  transformNavResources,
  transformSearchResults,
  transformSearchCategories,
  transformRoleLocations,
  mapSearchMode,
  mapSearchInFields,
} from './transformers';
// Re-export public helpers so existing import sites remain unchanged
export { transformNavCategories, resolveResourceUrl };

import {
  useGetNavCategoriesQuery,
  useGetNavResourcesQuery,
  useSearchLibraryQuery,
  useGetCategoryCreateInitQuery,
  useGetResourceCreateInitQuery,
  useGetCategoryEditInitQuery,
  useGetCategoryDeleteInitQuery,
  useLazyGetLocationTreeChildrenQuery,
  useGetParentCategoriesQuery,
  useLazyGetParentCategoriesByLocationQuery,
  useLazyGetParentCategoryChildrenQuery,
  useGetRoleLocationsQuery,
  useGetResourceEditInitQuery,
  useGetResourceStatisticsQuery,
  useCreateLibraryCategoryMutation,
  useUpdateLibraryCategoryMutation,
  useDeleteLibraryCategoryMutation,
  useCreateLibraryResourceMutation,
  useCreateLibraryResourceFileMutation,
  useUpdateLibraryResourceMutation,
  useUpdateLibraryResourceFileMutation,
  useDeleteLibraryResourceMutation,
} from './libraryApi';


// ═══════════════════════════════════════════════════════════════════════════
// Query hooks
// ═══════════════════════════════════════════════════════════════════════════

/** Lazy-load subcategories at a given path. */
export function useGetSubcategoriesQuery(
  lineage: string,
  skip: boolean = false,
  isManageMode: boolean = false,
) {
  const result = useGetNavCategoriesQuery(
    !skip ? { lineage, isManageMode } : skipToken
  );
  return {
    ...result,
    data: result.data ? transformNavCategories(result.data) : ([] as Category[]),
  };
}

/** Root-level sidebar categories. */
export function useGetCategoriesQuery(isManageMode: boolean = false) {
  const result = useGetNavCategoriesQuery({ lineage: '/Home/', isManageMode });
  return {
    ...result,
    data: result.data ? transformNavCategories(result.data) : ([] as Category[]),
  };
}

/** Resource listing or search.
 *  Pass `isManageMode: true` when the user has entered manage mode so the API
 *  returns all resources (including those excluded at some locations). */
export function useGetResourcesQuery(
  params: ResourcesQueryParams & { isManageMode?: boolean },
  options?: { skip?: boolean }
) {
  const shouldSkip = options?.skip ?? false;
  const isSearching = !!params.q;
  const isManageMode = params.isManageMode ?? false;

  const realResourceResult = useGetNavResourcesQuery(
    !shouldSkip && !isSearching && params.categoryLineage
      ? { lineage: params.categoryLineage, category: params.categoryName ?? '', isManageMode }
      : skipToken
  );

  const realSearchResult = useSearchLibraryQuery(
    !shouldSkip && isSearching
      ? {
          q: params.q!,
          searchType: mapSearchMode(params.searchMode),
          searchIn: mapSearchInFields(params.searchIn),
          isManageMode,
        }
      : skipToken
  );

  if (isSearching) {
    return {
      data: realSearchResult.data ? transformSearchResults(realSearchResult.data) : undefined,
      searchCategoryMatches: realSearchResult.data
        ? transformSearchCategories(realSearchResult.data)
        : ([] as SearchCategoryMatch[]),
      isLoading: realSearchResult.isLoading,
      isFetching: realSearchResult.isFetching,
      isUninitialized: realSearchResult.isUninitialized,
      isError: realSearchResult.isError,
    };
  }

  return {
    data: realResourceResult.data ? transformNavResources(realResourceResult.data) : undefined,
    searchCategoryMatches: [] as SearchCategoryMatch[],
    isLoading: realResourceResult.isLoading,
    isFetching: realResourceResult.isFetching,
    isUninitialized: realResourceResult.isUninitialized,
    isError: realResourceResult.isError,
  };
}

/** Usage statistics (created/modified by, last accessed, hit counts) for the
 *  resource details panel. `id` is the Resource.id string; skipped when absent. */
export function useResourceStatisticsQuery(id: string | undefined, options?: { skip?: boolean }) {
  const shouldSkip = options?.skip ?? false;
  return useGetResourceStatisticsQuery(
    !shouldSkip && id ? Number(id) : skipToken,
    // A resource's hit counts change every time it is opened, so always refetch
    // when the details panel (re)opens instead of serving the cached numbers.
    // Pairs with keepUnusedDataFor: 0 on the endpoint (cache dropped on close).
    { refetchOnMountOrArgChange: true },
  );
}

/** Init data for the Edit Resource page — single API call.
 *  Returns locationTree, categoryTree, roleLocations, assignedRoleLocationIds,
 *  and all resource fields needed to pre-populate the form. */
export function useResourceEditInitQuery(id: string, options?: { skip?: boolean }) {
  const shouldSkip = options?.skip ?? false;
  const result = useGetResourceEditInitQuery(
    !shouldSkip && id ? { idResource: Number(id) } : skipToken,
    // Always refetch on mount — the resource may have been edited since the cache was filled.
    { refetchOnMountOrArgChange: true },
  );

  const locations = React.useMemo(
    () => result.data
      ? result.data.locationTree.map((n) => ({ id: n.id, name: n.name, idParent: n.idParent, hasChildren: n.hasChildren }))
      : ([] as FlatTreeNode[]),
    [result.data],
  );

  const categories = React.useMemo(
    () => result.data
      ? result.data.categoryTree.map((n) => ({ id: n.id, name: n.name, idParent: n.idParent, hasChildren: n.hasChildren }))
      : ([] as FlatTreeNode[]),
    [result.data],
  );

  const roles: Role[] = React.useMemo(
    () => result.data
      ? result.data.roleLocations.map((rl) => ({ id: rl.id, name: rl.name }))
      : [],
    [result.data],
  );

  return {
    ...result,
    locations,
    categories,
    roles,
    assignedRoleLocationIds: result.data?.assignedRoleLocationIds ?? [],
    canExcludeLocations: result.data?.canExcludeLocations ?? false,
  };
}

/** Init data for Create/Edit Category pages.
 *  Returns locationTree (with hasChildren), parentCategoryTree, and
 *  default selection ids — all scoped to the given lineage.
 *  Re-call with a new lineage whenever the selected location changes. */
export function useCategoryCreateInitQuery(lineage: string | undefined, options?: { skip?: boolean }) {
  const result = useGetCategoryCreateInitQuery(
    lineage ?? '/Home/',
    { skip: options?.skip || !lineage }
  );

  const locations = React.useMemo(
    () => result.data
      ? result.data.locationTree.map((n) => ({ id: n.id, name: n.name, idParent: n.idParent, hasChildren: n.hasChildren }))
      : ([] as FlatTreeNode[]),
    [result.data],
  );

  const parentCategories = React.useMemo(
    () => result.data
      ? result.data.parentCategoryTree.map((n) => ({ id: n.id, name: n.name, idParent: n.idParent }))
      : ([] as FlatTreeNode[]),
    [result.data],
  );

  return {
    ...result,
    locations,
    parentCategories,
    defaultLocationId: result.data?.idLocation ?? null,
    defaultCategoryId: result.data?.idCategory ?? null,
  };
}

/** Init data for the Create Resource page.
 *  Returns locationTree (with hasChildren), parentCategoryTree, and
 *  default selection ids — all scoped to the given lineage.
 *  Uses the resource-create-init endpoint. */
export function useResourceCreateInitQuery(lineage: string | undefined, options?: { skip?: boolean }) {
  const result = useGetResourceCreateInitQuery(
    lineage ?? '/Home/',
    { skip: options?.skip || !lineage }
  );

  const locations = React.useMemo(
    () => result.data
      ? result.data.locationTree.map((n) => ({ id: n.id, name: n.name, idParent: n.idParent, hasChildren: n.hasChildren }))
      : ([] as FlatTreeNode[]),
    [result.data],
  );

  const parentCategories = React.useMemo(
    () => result.data
      ? result.data.categoryTree.map((n) => ({ id: n.id, name: n.name, idParent: n.idParent }))
      : ([] as FlatTreeNode[]),
    [result.data],
  );

  const roleLocations = React.useMemo(
    () => result.data?.roleLocations ?? [],
    [result.data],
  );

  return {
    ...result,
    locations,
    parentCategories,
    roleLocations,
    defaultLocationId: result.data?.idLocation ?? null,
    defaultCategoryId: result.data?.idCategory ?? null,
    canExcludeLocations: result.data?.canExcludeLocations ?? false,
  };
}

/** Init data for the Edit / Delete Category page.
 *  Fetches category details via category-edit-init.
 *  Pass `idCategory` to re-scope the response to a specific category instance
 *  (used when the user picks a different "Which Location?" option). RTK Query
 *  keys the cache on the args, so changing `idCategory` refetches and the hook
 *  always reflects the latest selection — no manual race handling needed.
 *  Pass skip=true to defer the query until both params are available. */
export function useCategoryEditInitQuery(
  editLineage: string | undefined,
  editCategory: string | undefined,
  idCategory?: number | null,
  options?: { skip?: boolean },
) {
  const result = useGetCategoryEditInitQuery(
    {
      editLineage: editLineage ?? '/Home/',
      editCategory: editCategory ?? '',
      ...(idCategory != null ? { idCategory } : {}),
    },
    {
      skip: options?.skip || !editLineage || !editCategory,
      // Always refetch on mount — the category may have been edited since the cache was filled.
      refetchOnMountOrArgChange: true,
    },
  );

  const locationTreeNodes = React.useMemo(
    () => result.data
      ? result.data.locationTree.map((n) => ({ id: n.id, name: n.name, idParent: n.idParent, hasChildren: n.hasChildren }))
      : ([] as FlatTreeNode[]),
    [result.data],
  );

  const parentCategories = React.useMemo(
    () => result.data
      ? result.data.parentCategoryTree.map((n) => ({ id: n.id, name: n.name, idParent: n.idParent }))
      : ([] as FlatTreeNode[]),
    [result.data],
  );

  // Derive the parent category ID from the parentCategoryTree for the selected category
  const defaultParentCategoryId = React.useMemo(() => {
    if (!result.data) return null;
    const selectedId = result.data.selectedCategoryId;
    const node = result.data.parentCategoryTree.find((n) => n.id === selectedId);
    return node?.idParent ?? null;
  }, [result.data]);

  // Build backward-compatible "locations" for DeleteCategoryPage (radio list)
  const categoryLocations = React.useMemo(() => {
    if (!result.data) return [];
    return result.data.categorySelector.map((item) => ({
      idLocation: item.idLocation,
      locationPath: item.locationName,
      isSelected: item.idLocation === result.data!.selectedIdLocation,
    }));
  }, [result.data]);

  return {
    ...result,
    // Expose raw data with backward-compatible locations for DeleteCategoryPage
    data: result.data ? { ...result.data, locations: categoryLocations } : undefined,
    // Processed fields for EditCategoryPage
    categorySelector: result.data?.categorySelector ?? [],
    locationTreeNodes,
    parentCategories,
    categoryId: result.data?.selectedCategoryId ?? null,
    defaultLocationId: result.data?.selectedIdLocation ?? null,
    defaultParentCategoryId,
  };
}

/** Init data for the Delete Category page.
 *  Fetches location-specific category instances via category-delete-init. */
export function useCategoryDeleteInitQuery(
  editLineage: string | undefined,
  editCategory: string | undefined,
) {
  const result = useGetCategoryDeleteInitQuery(
    { editLineage: editLineage ?? '/Home/', editCategory: editCategory ?? '' },
    {
      skip: !editLineage || !editCategory,
      // Always refetch on mount — child counts change when dependents are deleted/created.
      refetchOnMountOrArgChange: true,
    },
  );
  return result;
}

/** Roles for the Create/Edit Resource page. */
export function useGetRolesQuery(locationId?: number) {
  const result = useGetRoleLocationsQuery(
    locationId != null ? { idLocation: locationId } : skipToken
  );
  return {
    ...result,
    data: result.data ? transformRoleLocations(result.data) : ([] as Role[]),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Mutation hooks
// ═══════════════════════════════════════════════════════════════════════════

export function useCreateCategoryMutation() {
  const [trigger, result] = useCreateLibraryCategoryMutation();
  const wrappedTrigger = (params: Partial<Category>) =>
    trigger({ name: params.name ?? '', idLocation: 0, idParent: params.parentId ? Number(params.parentId) : 0, lineage: '/' });
  return [wrappedTrigger, result] as const;
}

export function useUpdateCategoryMutation() {
  const [trigger, result] = useUpdateLibraryCategoryMutation();
  const wrappedTrigger = (cat: Category) =>
    trigger({ id: Number(cat.id), name: cat.name, idParent: cat.parentId ? Number(cat.parentId) : 0, idLocation: 0, displayColumns: 0 });
  return [wrappedTrigger, result] as const;
}

export function useDeleteCategoryMutation() {
  const [trigger, result] = useDeleteLibraryCategoryMutation();
  const wrappedTrigger = (id: string) => trigger(Number(id));
  return [wrappedTrigger, result] as const;
}

export function useUpdateAdminResourceMutation() {
  const [triggerJson, jsonResult] = useUpdateLibraryResourceMutation();
  const [triggerFile, fileResult] = useUpdateLibraryResourceFileMutation();

  const wrappedTrigger = (payload: UpdateAdminResourcePayload) => {
    // File resource with a NEW file selected → multipart PUT
    if (payload.resourceType === 'File' && payload.file) {
      // Build multipart FormData — field names must match the backend's ToLowerInvariant() switch.
      const fd = new FormData();
      fd.append('name', payload.name);
      fd.append('description', payload.description);
      fd.append('idcategory', String(payload.categoryId));
      fd.append('idlocation', String(payload.locationId));
      fd.append('iscascading', payload.isCascading ? 'true' : 'false');
      fd.append('ispublic', payload.isPublic ? 'true' : 'false');
      payload.roleIds.forEach((id) => fd.append('rolelocationid', String(id)));
      fd.append('excludedlocations', JSON.stringify(payload.excludedLocations ?? []));
      fd.append('file', payload.file, payload.file.name);
      return triggerFile({ id: Number(payload.id), formData: fd });
    }

    // URL resource, or file resource keeping the existing file → JSON PUT.
    // For the keep-existing-file case, pass existingFilename back as `url` so
    // the backend retains the stored file path instead of clearing it.
    // `isFile: true` is required so the backend does not reclassify the
    // resource as a URL type when it sees a url field in the body.
    return triggerJson({
      id: Number(payload.id),
      name: payload.name,
      description: payload.description,
      url: payload.resourceType === 'URL' ? payload.url : payload.existingFilename,
      isFile: payload.resourceType === 'File',
      idCategory: payload.categoryId,
      idLocation: payload.locationId,
      isCascading: payload.isCascading,
      isPublic: payload.isPublic,
      roleLocationIds: payload.roleIds,
      excludedLocations: payload.excludedLocations ?? [],
    });
  };

  // Expose the active result — file result takes precedence if a file mutation is in-flight.
  const result = fileResult.isLoading || fileResult.isSuccess || fileResult.isError
    ? fileResult
    : jsonResult;

  return [wrappedTrigger, result] as const;
}

export function useDeleteResourceMutation() {
  const [trigger, result] = useDeleteLibraryResourceMutation();
  const wrappedTrigger = (id: string) => trigger(Number(id));
  return [wrappedTrigger, result] as const;
}

export function useCreateAdminCategoryMutation() {
  const [trigger, result] = useCreateLibraryCategoryMutation();
  const wrappedTrigger = (payload: CreateCategoryPayload) =>
    trigger({
      name: payload.name,
      idLocation: payload.locationId,
      idParent: payload.parentCategoryId ?? 0,
      lineage: payload.lineage,
      displayColumns: 1,
      excludedLocations: payload.excludedLocations,
    });
  return [wrappedTrigger, result] as const;
}

export function useCreateAdminResourceMutation() {
  const [triggerJson, jsonResult] = useCreateLibraryResourceMutation();
  const [triggerFile, fileResult] = useCreateLibraryResourceFileMutation();

  const wrappedTrigger = (payload: CreateAdminResourcePayload) => {
    if (payload.resourceType === 'File' && payload.file) {
      // Build multipart FormData — field names must match the backend's ToLowerInvariant() switch.
      const fd = new FormData();
      fd.append('name', payload.name);
      fd.append('description', payload.description);
      fd.append('idcategory', String(payload.categoryId));
      fd.append('idlocation', String(payload.locationId));
      fd.append('iscascading', payload.isCascading ? 'true' : 'false');
      fd.append('ispublic', payload.isPublic ? 'true' : 'false');
      payload.roleIds.forEach((id) => fd.append('rolelocationid', String(id)));
      fd.append('excludedlocations', JSON.stringify(payload.excludedLocations ?? []));
      fd.append('file', payload.file, payload.file.name);
      return triggerFile(fd);
    }
    // URL resource — plain JSON path
    return triggerJson({
      name: payload.name,
      description: payload.description,
      url: payload.url,
      idCategory: payload.categoryId,
      idLocation: payload.locationId,
      isCascading: payload.isCascading,
      isPublic: payload.isPublic,
      roleLocationIds: payload.roleIds,
      excludedLocations: payload.excludedLocations ?? [],
    });
  };

  // Expose the active result — file result takes precedence if a file mutation is in-flight.
  const result = fileResult.isLoading || fileResult.isSuccess || fileResult.isError
    ? fileResult
    : jsonResult;

  return [wrappedTrigger, result] as const;
}

export function useUpdateAdminCategoryMutation() {
  const [trigger, result] = useUpdateLibraryCategoryMutation();
  const wrappedTrigger = (payload: {
    id: string;
    name: string;
    parentCategoryId: number | null;
    locationId: number;
    displayColumns: number;
    excludedLocations: ExcludedLocation[];
  }) =>
    trigger({
      id: Number(payload.id),
      name: payload.name,
      idParent: payload.parentCategoryId ?? 0,
      idLocation: payload.locationId,
      displayColumns: payload.displayColumns,
      excludedLocations: payload.excludedLocations,
    });
  return [wrappedTrigger, result] as const;
}

/**
 * Hook that provides a lazy-load callback for location tree expansion.
 * Pages should maintain a `locationNodes` state array and pass the returned
 * `handleLoadChildren` as `onLoadChildren` to `<TreeSelect>`.
 *
 * Usage:
 * ```
 * const [locationNodes, setLocationNodes] = useState<FlatTreeNode[]>([]);
 * const handleLoadChildren = useLocationTreeLazyLoader(setLocationNodes);
 * <TreeSelect nodes={locationNodes} onLoadChildren={handleLoadChildren} ... />
 * ```
 */
export function useLocationTreeLazyLoader(
  setNodes: React.Dispatch<React.SetStateAction<FlatTreeNode[]>>,
) {
  const [fetchChildren] = useLazyGetLocationTreeChildrenQuery();

  const handleLoadChildren = React.useCallback(
    async (nodeId: number) => {
      try {
        const result = await fetchChildren({ isCreateMode: true, idParent: nodeId }).unwrap();
        if (result.length > 0) {
          setNodes((prev) => {
            // Avoid duplicates — only add nodes whose id is not already present
            const existingIds = new Set(prev.map((n) => n.id));
            const newNodes = result
              .filter((n) => !existingIds.has(n.id))
              .map((n) => ({ id: n.id, name: n.name, idParent: n.idParent, hasChildren: n.hasChildren, locationType: n.locationType }));
            return [...prev, ...newNodes];
          });
        } else {
          // API returned empty — mark parent as leaf so TreeSelect hides the expand arrow
          setNodes((prev) =>
            prev.map((n) => (n.id === nodeId ? { ...n, hasChildren: false } : n)),
          );
        }
      } catch {
        // Silently fail — the expand arrow will remain for retry
      }
    },
    [fetchChildren, setNodes],
  );

  return handleLoadChildren;
}

/**
 * Hook that fetches parent categories by location id and provides a lazy-load
 * callback for expanding category tree nodes.
 *
 * Usage:
 * ```
 * const { fetchCategoriesForLocation, handleLoadCategoryChildren } = useParentCategoryTree(setCategoryNodes);
 * // When user selects a location:
 * await fetchCategoriesForLocation(locationId);
 * // Pass to TreeSelect:
 * <TreeSelect nodes={categoryNodes} onLoadChildren={handleLoadCategoryChildren} ... />
 * ```
 */
export function useParentCategoryTree(
  setNodes: React.Dispatch<React.SetStateAction<FlatTreeNode[]>>,
) {
  const [fetchByLocation] = useLazyGetParentCategoriesByLocationQuery();
  const [fetchChildren] = useLazyGetParentCategoryChildrenQuery();

  const fetchCategoriesForLocation = React.useCallback(
    async (idLocation: number) => {
      try {
        const result = await fetchByLocation({ idLocation }).unwrap();
        setNodes(
          result.map((n) => ({
            id: n.id,
            name: n.name,
            idParent: n.idParent,
            hasChildren: n.hasChildren,
          })),
        );
      } catch {
        setNodes([]);
      }
    },
    [fetchByLocation, setNodes],
  );

  const handleLoadCategoryChildren = React.useCallback(
    async (nodeId: number) => {
      try {
        const result = await fetchChildren({ idParent: nodeId }).unwrap();
        if (result.length > 0) {
          setNodes((prev) => {
            const existingIds = new Set(prev.map((n) => n.id));
            const newNodes = result
              .filter((n) => !existingIds.has(n.id))
              .map((n) => ({
                id: n.id,
                name: n.name,
                idParent: n.idParent,
                hasChildren: n.hasChildren,
              }));
            return [...prev, ...newNodes];
          });
        } else {
          setNodes((prev) =>
            prev.map((n) => (n.id === nodeId ? { ...n, hasChildren: false } : n)),
          );
        }
      } catch {
        // Silently fail — expand arrow remains for retry
      }
    },
    [fetchChildren, setNodes],
  );

  return { fetchCategoriesForLocation, handleLoadCategoryChildren };
}